import { CheckCircle, ArrowRight, AlertTriangle, Wrench } from "lucide-react";

export const SpfDkimDmarcImplementation = () => {
  const steps = [
    {
      step: 1,
      title: "Audit Your Current Setup",
      description: "Use our Email Authentication Checker to see what records you have (or are missing).",
      action: "Run a check on your domain",
      tip: "Many domains have partial setups that can actually hurt deliverability"
    },
    {
      step: 2,
      title: "Configure SPF First",
      description: "List all services that send email on your behalf: ESP, CRM, transactional email, etc.",
      action: "Add SPF record to DNS",
      tip: "Keep it under 10 DNS lookups — use 'include:' strategically"
    },
    {
      step: 3,
      title: "Set Up DKIM for Each Sender",
      description: "Generate DKIM keys for each service and publish the public keys in DNS.",
      action: "Add DKIM records",
      tip: "Use 2048-bit keys and plan for annual rotation"
    },
    {
      step: 4,
      title: "Start DMARC in Monitor Mode",
      description: "Begin with p=none to collect reports without affecting delivery.",
      action: "Add DMARC with p=none",
      tip: "Set up a dedicated email or use a DMARC reporting service"
    },
    {
      step: 5,
      title: "Analyze Reports & Fix Issues",
      description: "Review DMARC reports to identify unauthorized senders or misconfigurations.",
      action: "Review aggregate reports",
      tip: "Look for 'fail' entries — they might be legitimate services you forgot"
    },
    {
      step: 6,
      title: "Gradually Enforce",
      description: "Move from p=none → p=quarantine → p=reject as confidence grows.",
      action: "Increase enforcement",
      tip: "Start with pct=10 to quarantine only 10% of failures initially"
    }
  ];

  const commonMistakes = [
    "Publishing SPF records that exceed the 10 DNS lookup limit",
    "Using weak 1024-bit DKIM keys (use 2048-bit)",
    "Jumping straight to p=reject without monitoring",
    "Forgetting to add third-party senders to SPF",
    "Not aligning DKIM signing domain with From: domain",
    "Ignoring DMARC reports — they reveal real issues"
  ];

  return (
    <section className="py-10 sm:py-12">
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-8 text-center">
        Implementation Guide
      </h2>
      
      <div className="space-y-4 mb-12">
        {steps.map((step, index) => (
          <div key={index} className="bg-card rounded-xl border border-border p-5 hover:border-primary/30 transition-colors">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold flex-shrink-0">
                {step.step}
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1">{step.title}</h3>
                <p className="text-sm text-muted-foreground mb-3">{step.description}</p>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                    <ArrowRight className="w-3 h-3" />
                    {step.action}
                  </span>
                  <span className="text-xs text-muted-foreground italic">💡 {step.tip}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-destructive/5 border border-destructive/20 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center text-destructive flex-shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground mb-3">Common Mistakes to Avoid</h3>
            <ul className="grid sm:grid-cols-2 gap-2">
              {commonMistakes.map((mistake, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                  {mistake}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        <a 
          href="/tools" 
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
        >
          <Wrench className="w-5 h-5" />
          Check Your Authentication
        </a>
        <p className="text-sm text-muted-foreground mt-2">Use our free Email Authentication Checker tool</p>
      </div>
    </section>
  );
};
